/**
 * AddScanIssuesModal — in-app confirm for the re-scan email's "Add N issues"
 * CTA. The email lands on the project issues page with
 * ?addScan=<shareId>&baseline=<id>&count=N&s=<token>; this renders the DS
 * ConfirmDialog OVER the loaded project with a selectable findings table
 * (all pre-checked — user feedback 2026-07-07): deselect any finding before
 * adding. Auth is the s= handoff token — the POST needs no Clerk header.
 * Opening never mutates; only the explicit confirm POSTs (selectedKeys).
 *
 * The table follows the house DataTable pattern (2026-09-09 teardown): a
 * header with select-all + the selected count + the Severity column, the
 * whole row as the toggle target, and the element crop per row (the house
 * IssueThumbnail) because one rule flagging several elements renders several
 * rows with one title, and the title alone cannot carry the choice.
 */
import { useEffect, useRef, useState } from 'react';
import { armIssueArrivals } from '../../hooks/issueArrivals';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { SelectDropdown } from '../shared/SelectDropdown';
import { Checkbox } from '../ui/Checkbox';
import { useProjects } from '../../hooks/useProjects';
import { ISSUE_MUTATION_INVALIDATE_KEYS } from '../../hooks/issueMutationInvalidation';
import { useAppSession } from '../../hooks/useAppSession';
import { SeverityBadge } from '../shared/badges/SeverityBadge';
import { IssueThumbnail } from '../shared/IssueThumbnail';
import type { TicketSeverity } from '@overlayqa/shared';
import { apiBaseUrl } from '../../lib/apiBaseUrl';
import { capture } from '../../lib/analytics';

interface AddScanIssuesModalProps {
  projectName: string;
}

interface ScanFinding {
  key: string;
  title: string;
  severity: string;
  ruleId: string;
  /** Per-element crop the free checker uploaded; null when none exists. */
  elementScreenshotUrl?: string | null;
  selector?: string | null;
  affectedCount?: number | null;
  /** Server-judged: an issue for this finding already exists in the target project. */
  inProject?: boolean;
}

/** Placeholder glyph for a row with no crop: the type the created issue gets. */
function placeholderTypeFor(scanType: string | null): string {
  return scanType === 'tokens' ? 'design-token' : 'accessibility';
}

function includeLabel(f: ScanFinding): string {
  return f.selector ? `Include: ${f.title} (${f.selector})` : `Include: ${f.title}`;
}

const VALID_SEVERITIES = new Set(['critical', 'high', 'medium', 'low']);

export function AddScanIssuesModal({ projectName }: AddScanIssuesModalProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [findings, setFindings] = useState<ScanFinding[] | null>(null);
  // Definitive server verdict that this link can never work: 401 = the s=
  // token is expired or was rewritten in transit (incident 2026-07-29: a mail
  // scanner character-substituted a real token), 404 = the report is gone.
  const [deadLinkStatus, setDeadLinkStatus] = useState<401 | 404 | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scanType, setScanType] = useState<string | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  // Project selector: defaults to the watch row's project (the route we
  // landed on); user can retarget to any project on their active team.
  const [targetProjectId, setTargetProjectId] = useState<string | null>(null);
  const { data: appSession } = useAppSession();
  const { data: teamProjects } = useProjects(appSession?.activeTeamId);

  const shareId = searchParams.get('addScan');
  const baseline = searchParams.get('baseline');
  const token = searchParams.get('s');

  // The link is FOR us as soon as addScan+baseline are present. The token is
  // deliberately NOT part of this test: it is the last param in the URL and is
  // 245 chars, so mail clients, link-wrappers and security scanners truncate it
  // first. Requiring it here meant a truncated link rendered nothing at all --
  // no dialog, no error, no telemetry -- which is indistinguishable from a dead
  // button and bypasses the dead-link dialog below (that only fires on a 401/404
  // from a fetch that never runs). See __tests__/AddScanIssuesModal.test.tsx.
  const addressedToUs = Boolean(shareId && baseline);
  const tokenMissing = addressedToUs && !token;
  const armed = addressedToUs && Boolean(token);
  // The project the marks and the POST target: the selector's choice when it
  // differs from the route's project, else the route's.
  const projectOverride = targetProjectId && targetProjectId !== routeProjectId ? targetProjectId : null;
  // Rows already in the project are never selectable; every count is over
  // the rest ("offerable").
  const offerable = (findings ?? []).filter((f) => !f.inProject);
  const allSelected = offerable.length > 0 && selected.size === offerable.length;
  const someSelected = selected.size > 0 && !allSelected;

  // Load the findings for the selectable table (read-only; token-authed).
  useEffect(() => {
    if (!armed) return;
    let cancelled = false;
    setDeadLinkStatus(null);
    (async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl()}/api/site-audits/${shareId}/scan-issues?s=${encodeURIComponent(token!)}&baseline=${baseline}` +
            (projectOverride ? `&project=${encodeURIComponent(projectOverride)}` : ''),
        );
        if (res.status === 401 || res.status === 404) {
          // The server has rejected this link for good; the add-all POST would
          // fail the same way, so a confirm dialog here is a doomed promise.
          if (!cancelled) {
            setDeadLinkStatus(res.status);
            capture('rescan_add_link_invalid', { status: res.status });
          }
          return;
        }
        if (!res.ok) throw new Error(`scan-issues ${res.status}`);
        const body = (await res.json()) as { data?: { findings?: ScanFinding[]; scanType?: string } };
        if (cancelled) return;
        const loaded = body.data?.findings ?? [];
        setFindings(loaded);
        setScanType(body.data?.scanType ?? null);
        // All pre-checked, except what the project already has.
        setSelected(new Set(loaded.filter((f) => !f.inProject).map((f) => f.key)));
      } catch {
        // Network blip / 5xx / 429: retrying the POST can genuinely work, so
        // keep the add-all fallback for these transient failures only.
        if (!cancelled) setFindings([]);
      }
    })();
    return () => { cancelled = true; };
    // Re-read when the selector retargets, so the in-project marks follow the
    // destination rather than describing the project the link landed on.
  }, [armed, shareId, baseline, token, projectOverride]);

  // A truncated link can never be recovered client-side (the token IS the
  // authorisation), so report it once and fall through to the dead-link dialog
  // rather than the doomed confirm.
  useEffect(() => {
    if (tokenMissing) capture('rescan_add_link_invalid', { status: 'missing_token' });
  }, [tokenMissing]);

  // The select-all box reads "mixed" while some rows are unticked; the DOM
  // property is the only way to draw it (no attribute exists).
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  if (!addressedToUs) return null;

  const fallbackCount = Number(searchParams.get('count')) || 0;
  const loaded = findings != null && findings.length > 0;
  const allInProject = loaded && offerable.length === 0;
  const selectedCount = loaded ? selected.size : fallbackCount;
  // "the found issues" is the FETCH-FAILED fallback (no table, no count param):
  // it must never fire on a zero the user chose by unticking, where the count
  // pattern holds ("Add 0 issues" under "Add issues to X?").
  const countLabel = loaded || selectedCount > 0
    ? `${selectedCount} issue${selectedCount !== 1 ? 's' : ''}`
    : 'the found issues';
  const titleLabel = loaded && selectedCount === 0 ? 'issues' : countLabel;


  const clearParams = () => {
    const next = new URLSearchParams(searchParams);
    ['addScan', 'baseline', 'count', 's'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  // A missing token lands here too: the existing copy ("some email security
  // tools change them so they stop working") already describes truncation, and
  // the recovery is identical -- open the email and use "View the full report".
  if (deadLinkStatus || tokenMissing) {
    const reportGone = deadLinkStatus === 404;
    return (
      <ConfirmDialog
        open
        title={reportGone ? 'This report is no longer available' : "This link isn't working"}
        message={
          reportGone
            ? 'The scan report behind this link has been removed.'
            : 'Add-to-project links expire after 14 days, and some email security tools change them so they stop working. Open your OverlayQA email and use the View the full report link to see the findings.'
        }
        confirmLabel="Got it"
        hideCancel
        onConfirm={clearParams}
        onCancel={clearParams}
        maxWidthClass="max-w-[432px]"
      />
    );
  }

  const chosenProjectId = targetProjectId ?? routeProjectId ?? null;
  const projectOptions = (teamProjects ?? [])
    .filter((p: { isDemo?: boolean }) => !p.isDemo)
    .map((p: { id: string; name: string }) => ({ value: p.id, label: p.name }));
  const chosenProjectName =
    projectOptions.find((o: { value: string }) => o.value === chosenProjectId)?.label ?? projectName;

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(offerable.map((f) => f.key)));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const useSelection = findings != null && findings.length > 0;
      const res = await fetch(`${apiBaseUrl()}/api/site-audits/${shareId}/create-issues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          s: token,
          baseline,
          ...(useSelection ? { selectedKeys: [...selected] } : {}),
          ...(chosenProjectId && chosenProjectId !== routeProjectId
            ? { projectId: chosenProjectId }
            : {}),
        }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          toast.error('This link has expired. Open the latest OverlayQA email for a fresh one.');
        } else if (res.status === 429) {
          toast.error('Too many requests right now. Wait a minute and try again.');
        } else {
          toast.error('Failed to add the issues. Please try again.');
        }
        return;
      }
      const body = (await res.json()) as {
        data?: { added?: number; already?: boolean; capped?: boolean; limit?: number; existing?: number };
      };
      const existing = body.data?.existing ?? 0;
      if (body.data?.already) {
        toast.success('These issues are already in your project');
      } else if (existing > 0 && !body.data?.capped) {
        // The server refused rows the project already had (a stale modal, or
        // a teammate added them meanwhile): say so beside the count.
        const added = body.data?.added ?? 0;
        toast.success(
          `${added} issue${added !== 1 ? 's' : ''} added, ${existing} ${existing === 1 ? 'was' : 'were'} already in your project`,
        );
      } else if (body.data?.capped) {
        // Honest partial add: the plan cap stopped the batch mid-way. A plain
        // success toast here would read as bait-and-switch (Congruence).
        const added = body.data?.added ?? 0;
        toast.error(
          `Added ${added} of ${selectedCount} issues. This project hit your plan's ${body.data.limit}-issue limit. Upgrade to add the rest.`,
        );
      } else {
        const added = body.data?.added ?? 0;
        toast.success(`${added} issue${added !== 1 ? 's' : ''} added`);
      }
      // Arm the cascade BEFORE the invalidate: the refetch it kicks off is what
      // brings the new rows, and the page must already be watching for them.
      armIssueArrivals();
      // Issues AND the summary caches: the four chart cards read
      // ['project-summary', projectId], which ['issues'] never prefix-matched,
      // so they sat on "No issues yet" for the full 60s staleTime while the
      // table below already showed the new rows (reported 2026-08-06).
      await Promise.all(
        ISSUE_MUTATION_INVALIDATE_KEYS.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [...queryKey] }),
        ),
      );
      if (chosenProjectId && chosenProjectId !== routeProjectId) {
        // Issues landed in a different project — take the user to them.
        navigate(`/projects/${chosenProjectId}/issues`, { replace: true });
      } else {
        clearParams();
      }
    } catch {
      toast.error('Failed to add the issues. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open
      title={`Add ${titleLabel} to ${chosenProjectName}?`}
      confirmLabel={`Add ${countLabel}`}
      confirmDisabled={loaded && selected.size === 0}
      onConfirm={handleConfirm}
      onCancel={clearParams}
      loading={submitting}
      maxWidthClass="max-w-[432px]"
    >
      {projectOptions.length > 1 && (
        <div className="mb-4">
          <label className="field-label">Project</label>
          <SelectDropdown
            value={chosenProjectId ?? ''}
            options={projectOptions}
            onChange={(v) => setTargetProjectId(v)}
            size="lg"
          />
        </div>
      )}
      {allInProject && (
        <p className="state-note mb-3">All of these are already in your project.</p>
      )}
      {findings == null ? (
        <p className="field-hint mb-5">Loading findings...</p>
      ) : findings.length > 0 ? (
        // Tall enough for a typical email's rows (8 at 48px plus the header)
        // before it scrolls: the 2026-09-09 screenshot showed 7 of 8 rows in a
        // 256px box with the eighth hidden behind a macOS overlay scrollbar.
        <div className="mb-5 max-h-[min(440px,calc(100vh_-_320px))] overflow-y-auto rounded-lg border border-gray-200">
          <table className="w-full text-xs table-fixed">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="w-8 p-0 align-middle">
                  {/* Label wraps the box so the cell padding is not a dead click
                      (DataTable.tsx header, same measured miss). */}
                  <label className="flex items-center pl-3 pr-0 py-2 cursor-pointer">
                    <Checkbox
                      ref={headerCheckboxRef}
                      size="sm"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                      aria-checked={someSelected ? 'mixed' : allSelected}
                    />
                  </label>
                </th>
                {/* 72px: the 56px crop plus the 8px gutter plus slack, because a
                    fixed-layout column can land a fraction under its width and
                    the img's max-width:100% then clips it to 55px (measured on the
                    mini, 2026-09-09: rendered=55x32 in a w-16 cell). */}
                <th className="w-[72px] py-2 pr-2">
                  <span className="sr-only">Screenshot</span>
                </th>
                <th className="py-2 pr-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  {selected.size} of {offerable.length} selected
                </th>
                <th className="w-20 py-2 pr-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {findings.map((f) => {
                const locked = f.inProject === true;
                return (
                // The whole row toggles; the checkbox cell stops propagation so
                // its own native toggle is not undone by the row handler. A row
                // already in the project is locked: unticked, disabled, labelled.
                <tr
                  key={f.key}
                  onClick={locked ? undefined : () => toggle(f.key)}
                  className={locked ? 'text-gray-400' : 'cursor-pointer hover:bg-gray-50/60'}
                >
                  <td className="w-8 p-0 align-middle">
                    <label
                      className={`flex items-center pl-3 pr-0 py-2 ${locked ? '' : 'cursor-pointer'}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        size="sm"
                        checked={!locked && selected.has(f.key)}
                        disabled={locked}
                        onChange={() => toggle(f.key)}
                        aria-label={locked ? `Already in project: ${f.title}` : includeLabel(f)}
                      />
                    </label>
                  </td>
                  <td className="w-[72px] py-2 pr-2 align-middle">
                    <IssueThumbnail
                      size="md"
                      screenshotUrl={f.elementScreenshotUrl}
                      type={placeholderTypeFor(scanType)}
                      title={f.title}
                    />
                  </td>
                  <td className={`py-2 pr-2 truncate align-middle ${locked ? 'text-gray-500' : 'text-gray-700'}`} title={f.title}>
                    {f.title}
                    {locked && <span className="block text-[10px] text-gray-500">Already in project</span>}
                  </td>
                  <td className="py-2 pr-3 w-20 text-right align-middle">
                    {VALID_SEVERITIES.has(f.severity) && (
                      <SeverityBadge severity={f.severity as TicketSeverity} />
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
